import { ReactNode, useEffect } from "react";
import { ThemeProvider as NextThemeProvider, useTheme } from "next-themes";
import { supabase } from "../integrations/supabase/client";

type ThemePreference = "light" | "dark" | "system";

const isThemePreference = (value: string | null | undefined): value is ThemePreference =>
  value === "light" || value === "dark" || value === "system";

const AuthThemePreferenceSync = () => {
  const { setTheme } = useTheme();

  useEffect(() => {
    let isUnmounted = false;

    const applyDbThemePreference = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id || isUnmounted) {
        return;
      }

      const { data: account } = await supabase
        .from("user_accounts")
        .select("theme_preference")
        .eq("user_id", session.user.id)
        .maybeSingle();

      const dbTheme = account?.theme_preference;
      if (!isUnmounted && isThemePreference(dbTheme)) {
        setTheme(dbTheme);
      }
    };

    void applyDbThemePreference();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user?.id) {
        return;
      }

      void applyDbThemePreference();
    });

    return () => {
      isUnmounted = true;
      subscription.unsubscribe();
    };
  }, [setTheme]);

  return null;
};

type AppThemeProviderProps = {
  children: ReactNode;
};

export const AppThemeProvider = ({ children }: AppThemeProviderProps) => (
  <NextThemeProvider
    attribute="class"
    defaultTheme="system"
    enableSystem
    storageKey="nfp-tracker-theme-preference"
  >
    <AuthThemePreferenceSync />
    {children}
  </NextThemeProvider>
);
